import { useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  handleUnselectProgram,
  selectMobileMC,
} from '../store/slices/mobileMasterControlSlice';

import styled, { css } from 'styled-components';

import {
  flexBoxCenter,
  flexDirectionColumn,
  justifyContentSpaceBetween,
  layerA,
  layerA180Deg,
  layerADark,
  layerB,
} from '../styles/commonStyles';
import { selectUnits } from '../store/slices/settings/unitsSlice';
import {
  essSpecificLocationUnselectMachinesHandler,
  handleUnSelectIndividualMachine,
  selectEssSwitch,
} from '../store/slices/essSwitchSlice';
import {
  selectTgsSwitch,
  tgsHandleUnSelectIndividualMachine,
  tgsSpecificLocationUnselectMachinesHandler,
} from '../store/slices/tgsSwitchSlice';
import {
  selectTesSwitch,
  tesHandleUnSelectIndividualMachine,
  tesSpecificLocationUnselectMachinesHandler,
} from '../store/slices/tesSwitchSlice';

import {
  handleCleanUpSelectedOne,
  handleLocationSelect,
  handleMachineSelect,
  handleSelectAll,
  handleSpecificLocationSelect,
} from '../store/slices/masterControlSelectSlice';
import { handleResetAllSelectBySwitch } from '../store/slices/masterControlBySwitchSelectSlice';
import { handleResetAllSelectByLocation } from '../store/slices/masterControlSelectByLocationSlice';
import { selectUserPermissions } from '../store/slices/userSlice';
import { loopMachinesHandler } from '../../helpers/ess-tgs-tes-mc';
import { PROGRAM_TITLES } from '../../helpers/ess-tgs-tes-mc/mainMessageBoxHandler';
import { EssTgsTesContext } from '../context/contextOfEssTgsTes';
import ScheduleCalendar from './controls/heatingScheduler/ScheduleCalendar';
import InputTempMessage from '../userMessages/inputTempMessage';
import Ats from '../masterControlSwitches/Ats';
import ShutOff from '../masterControlSwitches/ShutOff';
import { setInitialStateSelectBoxHandler } from '../../helpers/ess-tgs-tes-mc/setInitialStateSelectBoxHandler';
import { useZonesQuery } from '../../hooks/useZonesQuery';

const ProgramsComponent = ({
  isApply,
  setIsApply,
  selectionDispatchHandler,
}) => {
  const unitsStatus = useSelector(selectUnits);
  const { isF } = unitsStatus;

  const MCStatus = useSelector(selectMobileMC);
  const { selectedSwt, isProgramSelected } = MCStatus;

  const { essSwitch, flatEssSwitch } = useSelector(selectEssSwitch);
  const { tgsSwitch, flatTgsSwitch } = useSelector(selectTgsSwitch);
  const { tesSwitch, flatTesSwitch } = useSelector(selectTesSwitch);

  const permissions = useSelector(selectUserPermissions);

  const {
    messageBoxHandler,
    openMessageBox,
    setOpenMessageBox,
    message,
    programName,
  } = useContext(EssTgsTesContext);

  const [showInputMessage, setShowInputMessage] = useState(false);

  const { data: essZones } = useZonesQuery('ess');
  const { data: tgsZones } = useZonesQuery('tgs');
  const { data: tesZones } = useZonesQuery('tes');

  const [instantHeatTemp, setInstantHeatTemp] = useState('');
  const [constantTemp, setConstantTemp] = useState('');
  const [schedulerTemp, setSchedulerTemp] = useState('');
  const [openScheduler, setOpenScheduler] = useState(false);
  const [scheduleData, setScheduleData] = useState({
    start: { date: null, time: null },
    end: { date: null, time: null },
    inputTemp: schedulerTemp,
    isF: null,
  });

  const { start, end } = scheduleData;

  const displayStart =
    start.date !== null
      ? `${start.time.hour} : ${start.time.minute} ${
          start.time.division
        } - ${start.date.getDate()} / ${
          start.date.getMonth() + 1
        } / ${start.date.getFullYear()} `
      : ' -----------------';

  const displayEnd =
    end.date !== null
      ? `${end.time.hour} : ${end.time.minute} ${
          end.time.division
        } - ${end.date.getDate()} / ${
          end.date.getMonth() + 1
        } / ${end.date.getFullYear()} `
      : ' -----------------';

  const dispatch = useDispatch();

  const handleCleanUp = () => {
    dispatch(handleCleanUpSelectedOne());
    if (selectedSwt === 'ess') {
      selectionDispatchHandler(selectedSwt, essSwitch);
    } else if (selectedSwt === 'tes') {
      selectionDispatchHandler(selectedSwt, tesSwitch);
    } else if (selectedSwt === 'tgs') {
      selectionDispatchHandler(selectedSwt, tgsSwitch);
    }
  };

  const systems = {
    ess: {
      swt: 'ESS',
      zones: essZones,
      flat: flatEssSwitch,
      tree: essSwitch,
      unselect: handleUnSelectIndividualMachine,
      unselectSpecific: essSpecificLocationUnselectMachinesHandler,
    },
    tgs: {
      swt: 'TGS',
      zones: tgsZones,
      flat: flatTgsSwitch,
      tree: tgsSwitch,
      unselect: tgsHandleUnSelectIndividualMachine,
      unselectSpecific: tgsSpecificLocationUnselectMachinesHandler,
    },
    tes: {
      swt: 'TES',
      zones: tesZones,
      flat: flatTesSwitch,
      tree: tesSwitch,
      unselect: tesHandleUnSelectIndividualMachine,
      unselectSpecific: tesSpecificLocationUnselectMachinesHandler,
    },
  };

  const hasSelectedMachine = (flat) =>
    Object.values(flat ?? {}).some((location) =>
      Object.values(location).some((entry) =>
        entry?.deviceMac
          ? entry.isSelected
          : Object.values(entry ?? {}).some((machine) => machine?.isSelected),
      ),
    );

  const applyProgram = (program, temp = 0, data = null) => {
    const system = systems[selectedSwt];
    if (!system || !permissions.WRITE) {
      return false;
    }

    loopMachinesHandler(
      program,
      'on',
      'switch',
      'locations',
      temp,
      data,
      isF,
      system.zones,
      system.swt,
      system.flat,
      dispatch,
      system.unselect,
      system.unselectSpecific,
      handleResetAllSelectBySwitch,
      handleResetAllSelectByLocation,
      messageBoxHandler,
      system.tree,
    );

    return hasSelectedMachine(system.flat);
  };

  const rejectInput = (reason, program) => {
    messageBoxHandler(reason, 'switch', 'locations', PROGRAM_TITLES[program]);
    setShowInputMessage(true);
  };

  const finishApply = (applied) => {
    if (!applied) {
      return;
    }
    handleCleanUp();
    setIsApply(true);
  };

  const handleDispatchInstantHeat = () => {
    const temp = Number(instantHeatTemp);
    const minTemp = isF ? 250 : 121;
    const maxTemp = isF ? 1830 : 999;

    if (!instantHeatTemp || temp < minTemp || temp > maxTemp) {
      rejectInput('tempA', 'instantHeat');
      return;
    }

    const applied = applyProgram('instantHeat', temp);
    if (applied) {
      setInstantHeatTemp('');
    }
    finishApply(applied);
  };

  const handleDispatchSnowSensor = () => {
    finishApply(applyProgram('snowSensor'));
  };

  const handleDispatchWindFactor = () => {
    finishApply(applyProgram('windFactor'));
  };

  const handleDispatchOptionalConstantTemp = () => {
    const temp = Number(constantTemp);
    const minTemp = isF ? 76 : 24;
    const maxTemp = isF ? 249 : 121;

    if (!constantTemp || temp <= minTemp || temp >= maxTemp) {
      rejectInput('tempB', 'constantTemp');
      return;
    }

    const applied = applyProgram('constantTemp', temp);
    if (applied) {
      setConstantTemp('');
    }
    finishApply(applied);
  };

  const handleClear = () => {};

  const handleClose = () => {
    setOpenScheduler(false);
  };

  const handleSetNewSchedule = (data) => {
    setScheduleData(data);
    setOpenScheduler(false);
  };

  const handleDispatchHeatingSchedule = () => {
    const temp = Number(schedulerTemp);
    const minTemp = isF ? 250 : 121;
    const maxTemp = isF ? 1830 : 999;

    if (scheduleData.start.date === null || scheduleData.end.date === null) {
      rejectInput('selectB', 'heatingSchedule');
      return;
    }

    if (!schedulerTemp || temp < minTemp || temp > maxTemp) {
      rejectInput('tempA', 'heatingSchedule');
      return;
    }

    const applied = applyProgram('heatingSchedule', 0, {
      start: scheduleData.start,
      end: scheduleData.end,
      inputTemp: temp,
    });
    if (applied) {
      setSchedulerTemp('');
    }
    finishApply(applied);
  };

  const handleDispatchFanOnly = () => {
    finishApply(applyProgram('fanOnly'));
  };

  const handleDispatchAts = (selection) => {
    if (applyProgram('ats', 0, selection)) {
      handleCleanUp();
    }
  };

  const handleDispatchShutOff = () => {
    if (applyProgram('shutOff')) {
      handleCleanUp();
    }
  };

  return (
    <Wrapper>
      {isProgramSelected[0] && (
        <ContainerTypeA>
          <TitleWrapper>
            <Title>instant heat program</Title>
            <Icon src='/images/logo-instantHeat.svg' />
          </TitleWrapper>
          <SectionControllerTypeA>
            <InputWrapper>
              <Input
                value={instantHeatTemp}
                type='text'
                onChange={(e) => setInstantHeatTemp(e.target.value)}
                placeholder={isF ? '0 °F' : '0 °C'}
              />
            </InputWrapper>

            <InputWrapper isApply={isApply}>
              <ButtonTypeA onClick={handleDispatchInstantHeat}>
                <ButtonTypeATop>apply</ButtonTypeATop>
              </ButtonTypeA>
            </InputWrapper>
          </SectionControllerTypeA>
        </ContainerTypeA>
      )}
      {isProgramSelected[1] && selectedSwt === 'tgs' && (
        <ContainerTypeA>
          <TitleWrapper>
            <Title>fan only program</Title>
            <Icon src='/images/tgs-fanOnly.svg' />
          </TitleWrapper>
          <ButtonTypeB onClick={handleDispatchFanOnly}>
            <ButtonLayerA>
              <ButtonLayerB isApply={isApply}>
                <ButtonLayerC>
                  <ButtonTop>apply</ButtonTop>
                </ButtonLayerC>
              </ButtonLayerB>
            </ButtonLayerA>
          </ButtonTypeB>
        </ContainerTypeA>
      )}

      {isProgramSelected[2] && selectedSwt === 'tgs' && (
        <ContainerTypeC>
          <TitleWrapper>
            <Title>snow sensor program</Title>
            <Icon src='/images/logo-snowSensor.svg' />
          </TitleWrapper>
          <SectionControllerTypeC>
            <ConstantTempWrapper>
              <DisplayWrapper>
                <DisplayTitle>
                  default trigger <br></br>temperature
                </DisplayTitle>
                <Divider />
                <DisplayTemp>350 {isF ? '°F' : '°C'}</DisplayTemp>
              </DisplayWrapper>
              <BottomWrapper>
                <DisplaySpan isBig={true}>* </DisplaySpan>
                <DisplaySpan>change temperature in settings</DisplaySpan>
              </BottomWrapper>
            </ConstantTempWrapper>
          </SectionControllerTypeC>

          <ButtonTypeB onClick={handleDispatchSnowSensor}>
            <ButtonLayerA>
              <ButtonLayerB isApply={isApply}>
                <ButtonLayerC>
                  <ButtonTop>apply</ButtonTop>
                </ButtonLayerC>
              </ButtonLayerB>
            </ButtonLayerA>
          </ButtonTypeB>
        </ContainerTypeC>
      )}

      {isProgramSelected[1] && selectedSwt !== 'tgs' && (
        <ContainerTypeC>
          <TitleWrapper>
            <Title>snow sensor program</Title>
            <Icon src='/images/logo-snowSensor.svg' />
          </TitleWrapper>
          <SectionControllerTypeC>
            <ConstantTempWrapper>
              <DisplayWrapper>
                <DisplayTitle>
                  default trigger <br></br>temperature
                </DisplayTitle>
                <Divider />
                <DisplayTemp>350 {isF ? '°F' : '°C'}</DisplayTemp>
              </DisplayWrapper>
              <BottomWrapper>
                <DisplaySpan isBig={true}>* </DisplaySpan>
                <DisplaySpan>change temperature in settings</DisplaySpan>
              </BottomWrapper>
            </ConstantTempWrapper>
          </SectionControllerTypeC>

          <ButtonTypeB onClick={handleDispatchSnowSensor}>
            <ButtonLayerA>
              <ButtonLayerB isApply={isApply}>
                <ButtonLayerC>
                  <ButtonTop>apply</ButtonTop>
                </ButtonLayerC>
              </ButtonLayerB>
            </ButtonLayerA>
          </ButtonTypeB>
        </ContainerTypeC>
      )}

      {isProgramSelected[2] && selectedSwt !== 'tgs' && (
        <ContainerTypeA>
          <TitleWrapper>
            <Title>optional constant temp program</Title>
            <Icon src='/images/logo-constantTemp.svg' />
          </TitleWrapper>
          <SectionControllerTypeA>
            <InputWrapper>
              <Input
                value={constantTemp}
                type='text'
                onChange={(e) => setConstantTemp(e.target.value)}
                placeholder={isF ? '0 °F' : '0 °C'}
              />
            </InputWrapper>

            <InputWrapper isApply={isApply}>
              <ButtonTypeA onClick={handleDispatchOptionalConstantTemp}>
                <ButtonTypeATop>apply</ButtonTypeATop>
              </ButtonTypeA>
            </InputWrapper>
          </SectionControllerTypeA>
        </ContainerTypeA>
      )}

      {isProgramSelected[3] && (
        <ContainerTypeB>
          <TitleWrapper>
            <Title>heating schedule program</Title>
            <Icon src='/images/logo-schedule.svg' />
          </TitleWrapper>
          <SectionControllerTypeB>
            <ScheduleWrapper>
              <SectionSchedule>
                <SectionDisplay>
                  <ScheduleSpan>start date :</ScheduleSpan>
                  <DisplayDate>
                    <Date>{displayStart}</Date>
                  </DisplayDate>
                </SectionDisplay>
                <SectionDisplay>
                  <ScheduleSpan>end date :</ScheduleSpan>
                  <DisplayDate>
                    <Date>{displayEnd}</Date>
                  </DisplayDate>
                </SectionDisplay>
              </SectionSchedule>

              <SectionScheduleButton onClick={() => setOpenScheduler(true)}>
                <Icon src='/images/masterCtr-select-btn.svg' />
              </SectionScheduleButton>
            </ScheduleWrapper>
          </SectionControllerTypeB>

          <SectionControllerTypeA>
            <InputWrapper>
              <Input
                value={schedulerTemp}
                type='text'
                onChange={(e) => setSchedulerTemp(e.target.value)}
                placeholder={isF ? '0 °F' : '0 °C'}
              />
            </InputWrapper>

            <InputWrapper isApply={isApply}>
              <ButtonTypeA onClick={handleDispatchHeatingSchedule}>
                <ButtonTypeATop>apply</ButtonTypeATop>
              </ButtonTypeA>
            </InputWrapper>
          </SectionControllerTypeA>
          {openScheduler && (
            <ScheduleCalendarWrapper isMobile={true}>
              <ScheduleCalendar
                handleScheduler={handleSetNewSchedule}
                handleClose={handleClose}
                handleClear={handleClear}
                scheduleList={scheduleData}
                unit={isF}
              />
            </ScheduleCalendarWrapper>
          )}
        </ContainerTypeB>
      )}

      {isProgramSelected[4] && (
        <ContainerTypeA>
          <TitleWrapper>
            <Title>wind factor program</Title>
            <Icon src='/images/logo-windFactor.svg' />
          </TitleWrapper>
          <ButtonTypeB onClick={handleDispatchWindFactor}>
            <ButtonLayerA>
              <ButtonLayerB isApply={isApply}>
                <ButtonLayerC>
                  <ButtonTop>apply</ButtonTop>
                </ButtonLayerC>
              </ButtonLayerB>
            </ButtonLayerA>
          </ButtonTypeB>
        </ContainerTypeA>
      )}

      {isProgramSelected[5] && (
        <Ats
          swtName={selectedSwt}
          disabled={selectedSwt !== 'tgs'}
          scope='switch'
          handleOnClick={handleDispatchAts}
          isMasterControl={true}
        />
      )}
      {isProgramSelected[6] && (
        <ContainerTypeA>
          <TitleWrapper isCenter={true}>
            <Title isCenter={true}>mc. off</Title>
          </TitleWrapper>
          <ButtonTypeB onClick={handleDispatchShutOff}>
            <ButtonLayerA>
              <ButtonLayerB isApply={isApply}>
                <ButtonLayerC>
                  <ButtonTop>shut Off</ButtonTop>
                </ButtonLayerC>
              </ButtonLayerB>
            </ButtonLayerA>
          </ButtonTypeB>
        </ContainerTypeA>
      )}

      {(showInputMessage || openMessageBox) && (
        <MessageBoxWrapper>
          <InputTempMessage
            isMobile={true}
            onClose={() => {
              setShowInputMessage(false);
              setOpenMessageBox(false);
            }}
            title={'master control'}
            subtitle={programName}
            messages={message}
          />
        </MessageBoxWrapper>
      )}
    </Wrapper>
  );
};

export default ProgramsComponent;

const Wrapper = styled.div`
  margin: 4px 0 2px 0;
`;

const MessageBoxWrapper = styled.div`
  ${flexBoxCenter};

  position: fixed;
  inset: 0;
  z-index: 110;
`;

const ContainerTypeA = styled.section`
  width: 312px;
  height: 93px;
  border-radius: 2px;
  ${layerA180Deg};
  ${flexDirectionColumn};
  padding: 4px 0;
`;
const ContainerTypeB = styled.section`
  width: 312px;
  height: 180px;
  border-radius: 2px;
  ${layerA180Deg};
  ${flexDirectionColumn};
  padding: 4px 0;

  position: relative;
`;
const ContainerTypeC = styled.section`
  width: 312px;
  height: 176px;
  border-radius: 2px;
  ${layerA180Deg};
  ${flexDirectionColumn};
  padding: 4px 0;
`;
const TitleWrapper = styled.div`
  width: 303px;
  height: 32px;
  border-radius: 16px;
  ${layerADark};
  ${justifyContentSpaceBetween};
  padding: 0 2px 0 14px;

  ${(p) =>
    p.isCenter &&
    css`
      ${flexBoxCenter};
    `};
`;

const Title = styled.span`
  text-align: left;
  font-size: 10px;

  ${(p) =>
    p.isCenter &&
    css`
      text-align: center;
    `};
`;

const Icon = styled.img`
  height: 100%;
`;

const SectionControllerTypeA = styled.section`
  width: 303px;
  height: 41px;
  border-radius: 24px;
  ${layerA};
  ${justifyContentSpaceBetween};
  padding: 0 4px;
`;

const SectionControllerTypeB = styled.section`
  width: 303px;
  height: 78px;
  border-radius: 39px;

  ${layerADark};
  ${flexBoxCenter};
`;

const ScheduleWrapper = styled.div`
  width: 299px;
  height: 74px;
  border-radius: 37px;
  ${layerA180Deg};
  ${justifyContentSpaceBetween};
  padding: 0 12px 0 10px;
`;

const SectionSchedule = styled.section`
  width: 236px;
  height: 100%;
  ${flexDirectionColumn};
  padding: 8px 0;
`;

const SectionDisplay = styled.section`
  width: 100%;
  ${justifyContentSpaceBetween}
`;
const ScheduleSpan = styled.span`
  width: 32%;
  font-size: 9px;
  text-align: right;
`;
const DisplayDate = styled.div`
  width: 158px;
  height: 28px;
  border-radius: 14px;
  ${layerA}
  ${flexBoxCenter};
`;

const Date = styled.span`
  text-align: center;
  font-size: 10px;
`;
const SectionScheduleButton = styled.button`
  width: 32px;
  height: 29px;
  ${flexBoxCenter};
`;

const SectionControllerTypeC = styled.section`
  width: 303px;
  height: 71px;
  border-radius: 23px;
  ${layerADark};

  ${flexBoxCenter};
`;

const ConstantTempWrapper = styled.div`
  width: 299px;
  height: 67px;
  border-radius: 21px;
  ${layerA180Deg};
  ${flexDirectionColumn};
  padding: 5px 0;
`;

const DisplayWrapper = styled.div`
  width: 286px;
  height: 29px;
  border-radius: 22px;
  ${layerADark};
  ${justifyContentSpaceBetween};
  padding: 0 23px 0 20px;
`;
const DisplayTitle = styled.span`
  width: 123px;
  font-size: 10px;
  line-height: 10px;
`;

const DisplayTemp = styled.span`
  font-size: 14px;
  letter-spacing: 1.4px;
`;
const Divider = styled.div`
  height: 20px;
  width: 0;
  border: 1px solid #fff;
`;
const DisplaySpan = styled.span`
  font-size: 9px;
  ${(p) =>
    p.isBig &&
    css`
      font-size: 20px;
      margin-top: 4px;
    `}
`;
const BottomWrapper = styled.div`
  ${justifyContentSpaceBetween};
  width: 70%;
`;

const InputWrapper = styled.div`
  width: 142px;
  height: 34px;
  border-radius: 25px;
  ${layerA180Deg};
  ${flexBoxCenter};

  ${({ isApply }) =>
    isApply &&
    css`
      border-color: #95ff45;
    `}
`;
const Input = styled.input`
  width: 134px;
  height: 26px;
  border-radius: 18px;
  ${layerA};
  font-size: 12px;
  ::placeholder {
    color: #fff;
  }
`;

const ButtonTypeA = styled.button`
  width: 134px;
  height: 26px;
  border-radius: 18px;
  ${layerA};
`;
const ButtonTypeATop = styled.div`
  width: 132px;
  height: 24px;
  border-radius: 25px;
  ${layerA180Deg};
  ${flexBoxCenter};
  font-size: 12px;
`;

const ButtonTypeB = styled.button`
  width: 303px;
  height: 42px;
  border-radius: 24px;
  ${layerA};
  ${flexBoxCenter};
`;
const ButtonLayerA = styled.div`
  width: 297px;
  height: 37px;
  border-radius: 18px;
  ${layerB};
  ${flexBoxCenter};
`;
const ButtonLayerB = styled.div`
  width: 295px;
  height: 35px;
  border-radius: 25px;
  ${layerA180Deg};
  ${flexBoxCenter};
  ${({ isApply }) =>
    isApply &&
    css`
      border-color: #95ff45;
    `}
`;
const ButtonLayerC = styled.div`
  width: 287px;
  height: 27px;
  border-radius: 18px;
  ${layerA};
  ${flexBoxCenter};
`;
const ButtonTop = styled.div`
  width: 285px;
  height: 25px;
  border-radius: 25px;
  ${layerA180Deg};
  ${flexBoxCenter};
  font-size: 12px;
  text-align: center;
`;

const ScheduleCalendarWrapper = styled.div`
  width: 100%;
  ${flexBoxCenter}
  position: absolute;
  top: -150px;
  left: 0;
  z-index: 105;
`;

